import React, { useRef } from 'react'
import Icon from '../Icon'

const clamp = (v, a, b) => Math.max(a, Math.min(b, v))
let uid = 0
const nextId = () => ++uid

// Shows every PDF page. Draw a signature, then click any page to drop a copy of
// it there — you can place it on as many pages as you like. Each copy is
// draggable / resizable / removable. Placements: [{ id, page, xRatio, yRatio, wRatio }].
function SignPlacer({ pages, signature, placements, onChange }) {
  const pageRefs = useRef([])
  const drag = useRef(null)

  const defaultW = () => placements[placements.length - 1]?.wRatio || 0.3

  const onPageDown = (idx) => (e) => {
    if (!signature) return
    const rect = pageRefs.current[idx].getBoundingClientRect()
    const x = (e.clientX - rect.left) / rect.width
    const y = (e.clientY - rect.top) / rect.height
    const w = defaultW()
    onChange([...placements, { id: nextId(), page: idx, xRatio: clamp(x - w / 2, 0, 1 - w), yRatio: clamp(y - 0.03, 0, 0.98), wRatio: w }])
  }

  // Put the signature on every page (using the last placement as the template).
  const signAllPages = () => {
    if (!signature) return
    const tmpl = placements[placements.length - 1] || { xRatio: 0.6, yRatio: 0.8, wRatio: 0.28 }
    const taken = new Set(placements.map((p) => p.page))
    const adds = []
    pages.forEach((_, idx) => {
      if (!taken.has(idx)) adds.push({ id: nextId(), page: idx, xRatio: tmpl.xRatio, yRatio: tmpl.yRatio, wRatio: tmpl.wRatio })
    })
    onChange([...placements, ...adds])
  }

  const startDrag = (p, mode) => (e) => {
    e.stopPropagation()
    drag.current = { id: p.id, mode, rect: pageRefs.current[p.page].getBoundingClientRect(), startX: e.clientX, startY: e.clientY, orig: { ...p } }
    e.currentTarget.setPointerCapture?.(e.pointerId)
  }
  const move = (e) => {
    const d = drag.current
    if (!d) return
    const dx = (e.clientX - d.startX) / d.rect.width
    const dy = (e.clientY - d.startY) / d.rect.height
    onChange(placements.map((p) => {
      if (p.id !== d.id) return p
      if (d.mode === 'move') return { ...p, xRatio: clamp(d.orig.xRatio + dx, 0, 1 - p.wRatio), yRatio: clamp(d.orig.yRatio + dy, 0, 0.98) }
      return { ...p, wRatio: clamp(d.orig.wRatio + dx, 0.08, 0.92) }
    }))
  }
  const end = () => {
    drag.current = null
  }
  const removeP = (id) => (e) => {
    e.stopPropagation()
    onChange(placements.filter((p) => p.id !== id))
  }

  return (
    <div style={{ background: '#1f2430' }}>
      {/* toolbar */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,.08)', flexWrap: 'wrap' }}>
        <button className="btn btn-c" style={{ padding: '8px 14px', fontSize: 13 }} onClick={signAllPages} disabled={!signature}>
          <Icon name="check" size={15} style={{ color: '#fff' }} />Sign all pages
        </button>
        {placements.length > 0 && (
          <button className="btn" style={{ padding: '8px 14px', fontSize: 13, background: 'transparent', color: '#cbd5e1', border: '1px solid rgba(255,255,255,.2)' }} onClick={() => onChange([])}>
            Clear ({placements.length})
          </button>
        )}
        <span style={{ color: '#94a3b8', fontSize: 12.5, marginLeft: 'auto' }}>
          {signature ? 'Click a page to add the signature there.' : 'Draw your signature first →'}
        </span>
      </div>

      <div onPointerMove={move} onPointerUp={end} onPointerLeave={end} style={{ padding: 16, maxHeight: '60vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, alignItems: 'center' }}>
          {pages.map((src, idx) => (
            <div
              key={idx}
              ref={(el) => (pageRefs.current[idx] = el)}
              onPointerDown={onPageDown(idx)}
              style={{ position: 'relative', width: '100%', maxWidth: 520, lineHeight: 0, userSelect: 'none', touchAction: 'none', cursor: signature ? 'crosshair' : 'default', boxShadow: 'var(--shadow-lg)', borderRadius: 6 }}
            >
              <img src={src} alt={`Page ${idx + 1}`} draggable={false} style={{ width: '100%', display: 'block', borderRadius: 6 }} />
              <span style={{ position: 'absolute', top: 6, left: 6, background: 'rgba(0,0,0,.55)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 6, lineHeight: 1.4 }}>
                Page {idx + 1}
              </span>

              {signature &&
                placements
                  .filter((p) => p.page === idx)
                  .map((p) => (
                    <div
                      key={p.id}
                      onPointerDown={startDrag(p, 'move')}
                      style={{ position: 'absolute', left: `${p.xRatio * 100}%`, top: `${p.yRatio * 100}%`, width: `${p.wRatio * 100}%`, cursor: 'move', border: '1.5px dashed var(--c)', borderRadius: 4, background: 'rgba(255,255,255,.18)' }}
                    >
                      <img src={signature} alt="signature" draggable={false} style={{ width: '100%', display: 'block', pointerEvents: 'none' }} />
                      <span onPointerDown={removeP(p.id)} style={{ position: 'absolute', right: -10, top: -10, width: 20, height: 20, borderRadius: '50%', background: 'var(--pdf)', color: '#fff', display: 'grid', placeItems: 'center', cursor: 'pointer', border: '2px solid #fff' }}>
                        <Icon name="x" size={11} stroke={2.5} />
                      </span>
                      <span onPointerDown={startDrag(p, 'resize')} style={{ position: 'absolute', right: -8, bottom: -8, width: 16, height: 16, borderRadius: '50%', background: 'var(--c)', border: '2px solid #fff', cursor: 'nwse-resize' }} />
                    </div>
                  ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default SignPlacer
